import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import API, { formatApiError } from "../../lib/api";
import { Button } from "../ui/button";
import { Checkbox } from "../ui/checkbox";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "../ui/dialog";
import { SealCheck, HardHat, Warning, FirstAid, Users, ListChecks, ShieldCheck, ArrowSquareOut, Sparkle } from "@phosphor-icons/react";

const BAND = {
  very_high: "bg-red-600 text-white", high: "bg-orange-500 text-white", medium: "bg-amber-400 text-slate-900", low: "bg-emerald-500 text-white",
};
const BAND_LABEL = { very_high: "Very high", high: "High", medium: "Medium", low: "Low" };
const pretty = (k) => String(k || "").replace(/_/g, " ").replace(/^\w/, c => c.toUpperCase());

/** One-tap "Issue RAMS" next to the amber draft pill: a quick review sheet (hazards, method, PPE, permits, crew, A&E),
 *  a "checked" tick, then the same sign-off as the editor's Issue button. */
export default function QuickIssueRams({ ramsId, status, onIssued, testId = "quick-issue-rams", className = "" }) {
  const [open, setOpen] = useState(false);
  const [rams, setRams] = useState(null);
  const [checked, setChecked] = useState(false);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");

  useEffect(() => {
    if (!open || !ramsId) return;
    setRams(null); setChecked(false); setErr("");
    API.get(`/hs/rams/${ramsId}`).then(({ data }) => setRams(data)).catch(e => setErr(formatApiError(e)));
  }, [open, ramsId]);

  if (status !== "draft" || !ramsId) return null;

  const hazards = rams?.hazards || [];
  const steps = rams?.method_steps || [];
  const ops = rams?.operatives || [];
  const blockers = rams ? [!hazards.length && "at least one hazard", !steps.length && "a method statement"].filter(Boolean) : [];
  const worst = hazards.reduce((m, h) => Math.max(m, Number(h.residual || h.rating || 0)), 0);

  const issue = async () => {
    setBusy(true);
    try {
      const { data } = await API.post(`/hs/rams/${ramsId}/issue`, { quick: true });
      toast.success(`${data.reference} issued — crew notified${ops.length ? "" : " (no crew assigned yet)"}`);
      setOpen(false);
      onIssued?.(data);
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setBusy(false); }
  };

  return (
    <>
      <Button size="sm" onClick={() => setOpen(true)} className={`bg-amber-500 hover:bg-amber-600 text-slate-950 font-semibold ${className}`} data-testid={`${testId}-btn`}>
        <SealCheck size={14} weight="fill" className="mr-1" /> Issue RAMS
      </Button>
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto" data-testid={`${testId}-dialog`}>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2"><HardHat size={18} weight="fill" className="text-amber-500" /> Quick check before issuing</DialogTitle>
            <DialogDescription>Issuing signs this RAMS off in your name, supersedes any older issued RAMS on the job, asks the crew to acknowledge it and makes it downloadable in the client portal.</DialogDescription>
          </DialogHeader>
          {err && <p className="text-sm text-red-600" data-testid={`${testId}-error`}>{err}</p>}
          {!rams && !err && <p className="text-sm text-muted-foreground" data-testid={`${testId}-loading`}>Loading the RAMS…</p>}
          {rams && (
            <div className="space-y-3 text-sm">
              <div className="flex items-start justify-between gap-3">
                <div className="min-w-0">
                  <p className="font-mono text-xs text-muted-foreground">{rams.reference} · v{rams.version || 1}</p>
                  <p className="font-semibold leading-snug" data-testid={`${testId}-title`}>{rams.title}</p>
                  {rams.job?.property_address && <p className="text-xs text-muted-foreground">{rams.job.property_address}</p>}
                </div>
                <Link to={`/health-safety/rams/${ramsId}`} className="text-xs text-primary inline-flex items-center gap-1 shrink-0 hover:underline" data-testid={`${testId}-open-editor`}>Open editor <ArrowSquareOut size={12} /></Link>
              </div>
              {rams.auto && <p className="text-[11px] rounded-md bg-violet-50 dark:bg-violet-950/20 border border-violet-200 dark:border-violet-900/60 px-3 py-1.5 text-violet-900 dark:text-violet-200 inline-flex items-center gap-1.5" data-testid={`${testId}-auto-note`}><Sparkle size={12} weight="fill" /> Auto-prepared by AI from the job details — the AI cannot see the site.</p>}

              <div className="rounded-md border border-border">
                <div className="px-3 py-2 border-b border-border flex items-center justify-between">
                  <span className="text-xs font-semibold inline-flex items-center gap-1.5"><Warning size={14} weight="fill" className="text-amber-500" /> Hazards ({hazards.length})</span>
                  {worst > 0 && <span className={`text-[10px] px-2 py-0.5 rounded-full font-bold ${BAND[hazards.find(h => Number(h.residual || h.rating || 0) === worst)?.residual_band || hazards.find(h => Number(h.residual || h.rating || 0) === worst)?.band] || "bg-slate-200"}`} data-testid={`${testId}-worst`}>Highest residual risk {worst}</span>}
                </div>
                <ul className="max-h-48 overflow-y-auto divide-y divide-border" data-testid={`${testId}-hazards`}>
                  {hazards.map(h => (
                    <li key={h.id || h.hazard} className="px-3 py-1.5 flex items-start gap-2">
                      <span className={`mt-0.5 text-[10px] px-1.5 py-0.5 rounded font-bold shrink-0 ${BAND[h.residual_band || h.band] || "bg-slate-200"}`} title={`Initial ${h.rating} → residual ${h.residual}`}>{BAND_LABEL[h.residual_band || h.band] || h.residual || h.rating}</span>
                      <span className="text-xs leading-snug">{h.hazard}</span>
                    </li>
                  ))}
                  {!hazards.length && <li className="px-3 py-2 text-xs text-red-600">No hazards — add them in the editor before issuing.</li>}
                </ul>
              </div>

              <div className="grid grid-cols-2 gap-2 text-xs">
                <div className="rounded-md border border-border px-3 py-2" data-testid={`${testId}-method`}>
                  <p className="font-semibold inline-flex items-center gap-1.5"><ListChecks size={14} /> Method statement</p>
                  <p className="text-muted-foreground mt-0.5">{steps.length ? `${steps.length} step${steps.length === 1 ? "" : "s"}` : <span className="text-red-600">missing</span>}</p>
                </div>
                <div className="rounded-md border border-border px-3 py-2" data-testid={`${testId}-ppe`}>
                  <p className="font-semibold inline-flex items-center gap-1.5"><ShieldCheck size={14} /> PPE & permits</p>
                  <p className="text-muted-foreground mt-0.5 line-clamp-2">{(rams.ppe || []).map(pretty).join(", ") || "No PPE listed"}{(rams.permits || []).length ? ` · Permits: ${rams.permits.map(pretty).join(", ")}` : ""}</p>
                </div>
                <div className="rounded-md border border-border px-3 py-2" data-testid={`${testId}-crew`}>
                  <p className="font-semibold inline-flex items-center gap-1.5"><Users size={14} /> Crew to brief</p>
                  <p className="text-muted-foreground mt-0.5 line-clamp-2">{ops.length ? ops.map(o => o.name).join(", ") : <span className="text-amber-700 dark:text-amber-300">No crew assigned yet — they'll see it once assigned</span>}</p>
                </div>
                <div className="rounded-md border border-border px-3 py-2" data-testid={`${testId}-ae`}>
                  <p className="font-semibold inline-flex items-center gap-1.5"><FirstAid size={14} /> Nearest A&E</p>
                  <p className="text-muted-foreground mt-0.5 line-clamp-2">{rams.emergency?.hospital || <span className="text-amber-700 dark:text-amber-300">Not set</span>}{rams.emergency?.hospital_distance ? ` · ${rams.emergency.hospital_distance}` : ""}</p>
                </div>
              </div>

              {blockers.length > 0 && <p className="text-xs text-red-600" data-testid={`${testId}-blockers`}>Add {blockers.join(" and ")} in the editor before this can be issued.</p>}

              <label className="flex items-start gap-2 rounded-md border border-border px-3 py-2 cursor-pointer" data-testid={`${testId}-check-row`}>
                <Checkbox checked={checked} onCheckedChange={v => setChecked(!!v)} disabled={blockers.length > 0} data-testid={`${testId}-check`} className="mt-0.5" />
                <span className="text-xs">I have checked this RAMS — the hazards, controls and method are suitable and sufficient for the work and I'm signing it off.</span>
              </label>

              <div className="flex justify-end gap-2 pt-1">
                <Button variant="outline" size="sm" onClick={() => setOpen(false)} data-testid={`${testId}-cancel`}>Cancel</Button>
                <Button size="sm" onClick={issue} disabled={!checked || busy || blockers.length > 0} className="bg-emerald-600 hover:bg-emerald-700 text-white" data-testid={`${testId}-confirm`}>
                  <SealCheck size={14} weight="fill" className="mr-1" /> {busy ? "Issuing…" : `Issue ${rams.reference}`}
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </>
  );
}
